import { useAuth } from '@/contexts/AuthContext';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useOrgPlanLimits } from '@/hooks/useOrgPlanLimits';
import { canUsePhotoReports } from '@/lib/checklists';
import { JobChecklist } from './JobChecklist';
import { JobPhotos } from './JobPhotos';
import { JobCompletion } from './JobCompletion';
import { PhotoReportsUpgrade } from './PhotoReportsUpgrade';

interface JobWorkPanelProps {
  job: { id: string; status: string; client_email?: string | null };
  /** Called after a cleaner marks the job complete, so the calendar can refresh. */
  onChanged?: () => void;
}

/**
 * The "doing the job" part of the job dialog: checklist, before/after photos
 * and completion. Works out who's looking from auth, so the same panel serves
 * the cleaner on their phone and the owner in the office.
 */
export function JobWorkPanel({ job, onChanged }: JobWorkPanelProps) {
  const { user, staffRole } = useAuth();
  const { orgId, canWrite } = useAdminOrg();
  const { data: limits, isLoading: limitsLoading } = useOrgPlanLimits(orgId);

  const isManager = canWrite && (staffRole === 'owner' || staffRole === 'supervisor' || staffRole === 'super_admin');
  const canWork = canWrite && staffRole !== null;
  // If the plan can't be read, let the database decide (it refuses uploads on Solo).
  const photosAllowed = limits ? canUsePhotoReports(limits.effectivePlan) : !limitsLoading;
  const active = job.status !== 'cancelled';

  return (
    <div data-testid="job-work-panel" className="space-y-5 border-t border-border pt-4">
      <JobChecklist jobId={job.id} orgId={orgId} canTick={canWork && active} canEdit={isManager && active} />

      {limitsLoading ? null : photosAllowed ? (
        <JobPhotos jobId={job.id} orgId={orgId} userId={user?.id ?? null} canUpload={canWork && active} isManager={isManager} />
      ) : (
        <PhotoReportsUpgrade canUpgrade={staffRole === 'owner'} />
      )}

      <JobCompletion
        jobId={job.id}
        initialStatus={job.status}
        clientEmail={job.client_email ?? null}
        canMarkComplete={canWork && !isManager}
        canSendReport={isManager && photosAllowed && !limitsLoading}
        isManager={isManager}
        onCompleted={onChanged}
      />
    </div>
  );
}
